import React, { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { debounce } from "lodash";
import Grid from '@mui/material/Grid';
import { Box, IconButton } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddBoxIcon from '@mui/icons-material/AddBox';
import {
  TextInput, SubmitBar, FormStep, RadioButtons, CardLabel, RadioOrSelect, Card
  , Toast,
} from "@egovernments/digit-ui-react-components";

const InputText = React.forwardRef(({
  label, register, required, name, t, placeholder, grid = 6, disabled = false
}, ref) => (
  <Grid item xs={grid}>
    <CardLabel>{`${t(label)}`}</CardLabel>
    <TextInput
      t={t}
      type={"text"}
      optionKey="i18nKey"
      name={name}
      placeholder={placeholder}
      {...register(name)}
      disabled={disabled}
      readOnly={disabled}
      required={required}
      inputRef={ref}
    />
  </Grid>
));
const Select = React.forwardRef(({
  onChange, name, label, t, options, selectedOption, isDropDown = false, sortOptions = true, grid = 6
}, ref) => (
  <Grid item xs={grid}>
    <span className={"form-pt-dropdown-only"}>
      <CardLabel>{t(label)}</CardLabel>
      <RadioOrSelect
        optionCardStyles={{ maxHeight: "20vmax", overflow: "scroll" }}
        options={sortOptions ? options.sort((a, b) => a.name.localeCompare(b.name)) : options}
        optionKey="name"
        selectedOption={selectedOption}
        _ref={ref}
        name={name}
        onSelect={onChange}
        t={t}
        isDropDown={isDropDown}
        isDependent={true}
      />
    </span>
  </Grid>
))

const useTypeList = [
  {
    code: "hp.tcpsolan",
    id: 1,
    name: "Residential"
  },
  {
    code: "hp.tcpsolan",
    id: 2,
    name: "Commercial"
  },
  {
    code: "hp.tcpsolan",
    id: 3,
    name: "Industrial"
  },
  {
    code: "hp.tcpsolan",
    id: 4,
    name: "Public"
  }
]
const floorTypeList = [
  {
    code: "hp.tcpsolan",
    id: 1,
    name: "Basement"
  },
  {
    code: "hp.tcpsolan",
    id: 2,
    name: "Ground Floor"
  },
  {
    code: "hp.tcpsolan",
    id: 3,
    name: "1st Floor"
  },
  {
    code: "hp.tcpsolan",
    id: 4,
    name: "2nd Floor"
  },
  {
    code: "hp.tcpsolan",
    id: 5,
    name: "3rd Floor"
  },
  {
    code: "hp.tcpsolan",
    id: 6,
    name: "4th Floor"
  },
  {
    code: "hp.tcpsolan",
    id: 7,
    name: "5th Floor"
  },
  {
    code: "hp.tcpsolan",
    id: 8,
    name: "6th Floor"
  }
]
const typeOfLandVolvedYesList = [
  {
    code: "hp.tcpsolan",
    id: 1,
    name: "For change of Land use from the original Use"
  },
  {
    code: "hp.tcpsolan",
    id: 2,
    name: "IDP/Development Plan to Other Land Use"
  }
]
const devialtionTypeOptions = [
  { code: "approvedpermissibleupto10percent", i18nKey: "APPROVED_AND_PERMISSIBLE_UPTO_10", name: 'Plan was approved and deviations beyond Permissible limits upto the extent of 10%' },
  { code: "notapprovedpermissibleupto10percent", i18nKey: "NOT_APPROVED_AND_PERMISSIBLE_UPTO_10", name: ' Plan was not approved and deviations beyond permissible limits upto the extent of 10%' },
  { code: "notapprovedpermissiblewithoutdeviation", i18nKey: "NOT_APPROVED_AND_PERMISSIBLE_WT_DEVIATION", name: 'Plan was not approved and construction carried out without deviations' },
  { code: "underSizeConstruction", i18nKey: "UNDERSIZE_CONSTRUCTION_LESSTHAN_10", name: 'Building constructed on under size plot to the extent of 10 % less than the permissible plot size' },
]
const propertyTypeList = [
  {
    code: "hp.tcpsolan",
    id: 1,
    name: "Govt"
  },
  {
    code: "hp.tcpsolan",
    id: 2,
    name: "Private"
  },
  {
    code: "hp.tcpsolan",
    id: 3,
    name: "Public & Semi Public"
  }
]
const typeOfLandVolvedList = [
  {
    active: true,
    code: "YES",
    name: "Yes",
    i18nKey: "COMMON_MASTERS_YES"
  },
  {
    active: true,
    code: "NO",
    name: "No",
    i18nKey: "COMMON_MASTERS_NO"
  }
]

const DescriptionOfLand = ({ t, onSelect, formData, currStep }) => {
  const khasraTableEntry = { plotNo: 0, plotArea: 0, typeOfLand: null };
  const khasraWithAreaEntry = { khasraNo: 0, area: 0 };
  const buildUpAreaEntry = {
    id: 0,
    floorType: null,
    floorArea: 0,
    landUseType: null,
    parkingArea: 0,
    actualConstruction: 0,
    permissibleDeviation: 0,
    setbackDeviation: 0,
    deviation: 0,
  };
  const defaultValues = {
    revenueVillageMohal: formData[currStep.key]?.revenueVillageMohal || null,
    khataNo: formData[currStep.key]?.khataNo,
    khatauniNo: formData[currStep.key]?.khatauniNo,
    latitude: formData[currStep.key]?.latitude,
    longitude: formData[currStep.key]?.longitude,
    khasraWithArea: formData[currStep.key]?.khasraWithArea || [khasraWithAreaEntry],
    totalPlotArea: formData[currStep.key]?.totalPlotArea || '',
    noOfPlot: formData[currStep.key]?.noOfPlot,
    propertyType: formData[currStep.key]?.propertyType || null,
    useType: formData[currStep.key]?.useType || null,
    plotArea: formData[currStep.key]?.plotArea,
    maximumCoverage: formData[currStep.key]?.maximumCoverage,
    typeOfLandVolved: formData[currStep.key]?.typeOfLandVolved || null,
    typeOfCLUInvolved: formData[currStep.key]?.typeOfCLUInvolved || null,
    khasraTable: formData[currStep.key]?.khasraTable || [khasraTableEntry],
    typeOfDeviation: formData[currStep.key]?.typeOfDeviation || null,
    offenceRegistrationNumber: formData[currStep.key]?.offenceRegistrationNumber,
    offenceSubject: formData[currStep.key]?.offenceSubject,

    noOfBlocks: formData[currStep.key]?.noOfBlocks || 0,
    buildUpAreaTable: formData[currStep.key]?.buildUpAreaTable || [],
  };

  const {
    watch,
    control,
    register,
    setValue,
    getValues,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm({ defaultValues });
  const { fields: khasraWithArea, append, remove } = useFieldArray({
    control,
    name: "khasraWithArea",
  });
  const { fields: khasraTable, append: appendTable, remove: removeTable } = useFieldArray({
    control,
    name: "khasraTable",
  });
  const watchPlotArea = watch("khasraWithArea");
  const watchKhasraTable = watch("khasraTable");
  const watchNoOfBlocks = watch("noOfBlocks");
  const watchBuildUpAreaTable = watch("buildUpAreaTable");
  const watchMaximumCoverage = watch("maximumCoverage");

  const [useType, setUseType] = useState(formData[currStep.key]?.useType || null);
  const [propertyType, setPropertyType] = useState(formData[currStep.key]?.propertyType || null);
  const [typeOfLandVolved, setTypeOfLandVolved] = useState(formData[currStep.key]?.typeOfLandVolved || null);
  const [typeOfCLUInvolved, setTypeOfCLUInvolved] = useState(formData[currStep.key]?.typeOfCLUInvolved || null);
  const [typeOfDeviation, setTypeOfDeviation] = useState(formData[currStep.key]?.typeOfDeviation || null);
  const [showToast, setShowToast] = useState(null);

  const khasraWithAreaFields = khasraWithArea.map((field, index) => {
    return {
      ...field,
      ...watchPlotArea[index]
    };
  });
  const khasraTableFields = khasraTable.map((field, index) => {
    return {
      ...field,
      ...watchKhasraTable[index]
    };
  });

  useEffect(debounce(() => {
    if (Number(watchNoOfBlocks) > 0) {
      const mappedData = (count) => (
        Array.from(Array(Number(count)).keys()).map((i) => (
          { table: [buildUpAreaEntry] }
        ))
      );
      const noOfBlocks = Number(watchNoOfBlocks);
      let tableData = watchBuildUpAreaTable;

      if (tableData?.length > 0) {
        // Compare and add/show data from formData
        if (tableData.length < noOfBlocks) {
          // append diffrence of empty data into formData
          const diffrence = noOfBlocks - tableData?.length;
          const diffrenceData = mappedData(diffrence);
          tableData = [...tableData, ...diffrenceData];
        } else if (tableData.length > noOfBlocks) {
          // remove diffrence from formData
          tableData = tableData.filter((_, i) => noOfBlocks > i);
        }
      } else {
        // add empty array on first load
        tableData = mappedData(noOfBlocks);
      }

      setValue('buildUpAreaTable', tableData);
    }
  }, 50), [watchNoOfBlocks]);

  useEffect(() => {
    const total = khasraWithAreaFields.reduce((total, field) => {
      return total + Number(field.area);
    }, 0);

    setValue('totalPlotArea', Number(total));
  }, [khasraWithAreaFields]);

  useEffect(() => {
    if (Number(watchMaximumCoverage) > 0) {
      setShowToast(null)
    }
  }, [watchMaximumCoverage]);

  function selectedValue(value) {
    setTypeOfLandVolved(value);
    setValue('typeOfLandVolved', value);
  }
  function selectedValueUseType(value) {
    setUseType(value);
    setValue('useType', value);
  }
  function selectedValuePropertyType(value) {
    setPropertyType(value);
    setValue('propertyType', value);
  }
  function selectTypeOfCLUInvolved(value) {
    setTypeOfCLUInvolved(value);
    setValue('typeOfCLUInvolved', value);
  }
  function selectDeviationType(value) {
    setTypeOfDeviation(value)
    setValue('typeOfDeviation', value)
  }

  const addTableRow = debounce((tableIndex) => {
    const buildUpAreaData = [...watchBuildUpAreaTable];
    const nextIndex = buildUpAreaData[tableIndex].table.length;
    buildUpAreaData[tableIndex].table.splice(nextIndex, 0, { ...buildUpAreaEntry, ...{ id: nextIndex } });

    setValue('buildUpAreaTable', buildUpAreaData);
  }, 300);
  const removeTableRow = debounce((tableIndex, rowIndex) => {
    if (watchBuildUpAreaTable[tableIndex].table.length > 1) {
      const buildUpAreaData = [...watchBuildUpAreaTable];
      const filteredTable = buildUpAreaData[tableIndex].table;
      buildUpAreaData[tableIndex].table = filteredTable.filter(row => row.id !== rowIndex);

      setValue('buildUpAreaTable', buildUpAreaData);
    }
  }, 300);
  const updateBuildUpAreaData = debounce((tableIndex, rowIndex, key, value) => {
    const maximumCoverage = getValues('maximumCoverage');
    const calcPermissibleDeviation = (actualConstruction, floorArea) => {
      const calc = Number(actualConstruction) - Number(floorArea)
      if (isNaN(calc) || calc <= 0) { return 0 }
      return calc;
    };
    if (key === 'floorArea') {
      const actualConstruction = getValues(`buildUpAreaTable[${tableIndex}].table[${rowIndex}].actualConstruction`);
      if (maximumCoverage.length > 0) {
        setShowToast(null);
        if (Number(value) > Number(maximumCoverage)) {
          const errorMessage = 'Approved Floor Area Should be less than or equal to Maximum Permissible Ground Coverage(Sq.m)';
          setShowToast({ error: true, key: `${key}_${tableIndex}_${rowIndex}`, label: errorMessage });
        } else {
          setShowToast(null);
        }
      } else {
        setShowToast({ error: true, key: `${key}_${tableIndex}_${rowIndex}`, label: 'Maximum Permissible Ground Coverage(Sq.m) can not be left blank' });
        setFocus('maximumCoverage');
      }
      if (actualConstruction.length > 0) {
        const calcPermDev = calcPermissibleDeviation(actualConstruction, value);
        updateBuildUpAreaData(tableIndex, rowIndex, 'permissibleDeviation', calcPermDev);
      }
    }
    if (key === 'actualConstruction') {
      const floorArea = getValues(`buildUpAreaTable[${tableIndex}].table[${rowIndex}].floorArea`);
      const calcPermDev = calcPermissibleDeviation(value, floorArea);
      updateBuildUpAreaData(tableIndex, rowIndex, 'permissibleDeviation', calcPermDev);
    }

    const buildUpAreaData = [...watchBuildUpAreaTable];
    buildUpAreaData[tableIndex].table[rowIndex][key] = value;

    setValue('buildUpAreaTable', buildUpAreaData);
  }, 300);

  const onSkip = () => onSelect();
  const handleOnSubmit = (descriptionOfLand) => {
    console.info('buildUpAreaTable: form data on submit', descriptionOfLand, formData);
    onSelect(currStep.key, descriptionOfLand);
  }

  return (
    <div>
      <FormStep
        cardStyle={{ boxShadow: 'none', padding: 0, border: 0, overflowY: 'auto' }}
        _defaultValues={formData?.data}
        childrenAtTheBottom={false}
        onSelect={handleSubmit(handleOnSubmit)}
        onSkip={onSkip}
        t={t}
      >
        <div>
          <Grid container spacing={2}>
            <Select
              {...{
                label: "BPA_TYPE_OF_DEVIATION_CATEGORY_LABEL",
                register,
                required: false,
                name: "deviationType",
                t,
                options: devialtionTypeOptions,
                onChange: selectDeviationType,
                grid: 12
              }}
              isDropDown
            />

            {typeOfDeviation &&
              <Grid container item spacing={2}>
                {typeOfDeviation && typeOfDeviation?.code === "approvedpermissibleupto10percent" &&
                  <InputText
                    {...{
                      label: "BPA_OFFENCE_REGISTRATION_NUMBER",
                      register,
                      required: false,
                      name: "offenceRegistrationNumber",
                      t,
                      placeholder: "Enter Registration Number",
                    }}
                  />
                }
                <InputText
                  {...{
                    label: "BPA_OFFENCE_SUBJECT_LABEL",
                    register,
                    required: false,
                    name: "offenceSubject",
                    t,
                    placeholder: "Enter Subject",
                    grid: typeOfDeviation && typeOfDeviation?.code === "approvedpermissibleupto10percent" ? 6 : 12
                  }}
                />
              </Grid>
            }

            <InputText
              {...{
                label: "BPA_DOL_REVENUE_VILLAGE_MOHALLA_LABEL",
                register,
                required: false,
                name: "revenueVillageMohal",
                t,
                placeholder: "Enter Revenue Village Mohalla"
              }}
            />
            <InputText
              {...{
                label: "BPA_DOL_KHATA_NO_LABEL",
                register,
                required: false,
                name: "khataNo",
                t,
                placeholder: "Enter khata No"
              }}
            />
            <InputText
              {...{
                label: "BPA_DOL_KHATAUNI_NO_LABEL",
                register,
                required: false,
                name: "khatauniNo",
                t,
                placeholder: "Enter khatauni No"
              }}
            />

            <Grid item xs={6} container spacing={2}>
              <InputText
                {...{
                  label: "BPA_DOL_LATITUDE_LABEL",
                  register,
                  required: false,
                  name: "latitude",
                  t,
                  placeholder: "Enter latitude"
                }}
              />
              <InputText
                {...{
                  label: "BPA_DOL_LONGITUDE_LABEL",
                  register,
                  required: false,
                  name: "longitude",
                  t,
                  placeholder: "Enter longitude"
                }}
              />
            </Grid>

            {khasraWithAreaFields.map((field, index) => {
              return (
                <Grid item container key={field.id} spacing={2}>
                  <Grid item xs={6}>
                    <CardLabel>{`${t("BPA_DOL_KHASARA_NO_LABEL")}`}</CardLabel>
                    <TextInput
                      {...register(`khasraWithArea.${index}.khasraNo`)}
                      name={`khasraWithArea.${index}.khasraNo`}
                      t={t}
                      type={"text"}
                      optionKey="i18nKey"
                    />
                  </Grid>
                  <Grid item xs={6} container sx={{ alignItems: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <CardLabel>{`${t("BPA_DOL_AREA_SQM_LABEL")}`}</CardLabel>
                      <TextInput
                        {...register(`khasraWithArea.${index}.area`)}
                        name={`khasraWithArea.${index}.area`}
                        t={t}
                        type={"text"}
                        optionKey="i18nKey"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: '.5rem', mt: ".9rem", ml: '.5rem' }}>
                      <IconButton color="primary" aria-label="add" onClick={() => append(khasraWithAreaEntry)}>
                        <AddBoxIcon />
                      </IconButton>
                      {khasraWithArea.length > 1 && (
                        <IconButton color="error" aria-label="delete" onClick={() => remove(index)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              )
            })}

            <InputText
              {...{
                label: "BPA_DOL_TOTAL_PLOT_AREA_LABEL",
                register,
                required: false,
                name: "totalPlotArea",
                t,
                placeholder: "Enter Total Plot Area"
              }}
              disabled
            />
            <InputText
              {...{
                label: "BPA_DOL_NO_OF_PLOT_LABEL",
                register,
                required: false,
                name: "noOfPlot",
                t,
                placeholder: "Enter Number of Plot"
              }}
            />
            <Select
              {...{
                label: "CORE_COMMON_PROPERTY_TYPE",
                register,
                required: false,
                name: "propertyType",
                t,
                selectedOption: propertyType,
                options: propertyTypeList,
                onChange: selectedValuePropertyType
              }}
            />
            <Select
              {...{
                label: "CORE_COMMON_USE_TYPE",
                register,
                required: false,
                name: "useType",
                t,
                selectedOption: useType,
                options: useTypeList,
                onChange: selectedValueUseType
              }}
            />
            <InputText
              {...{
                label: "CORE_COMMON_PLOT_AREA",
                register,
                required: false,
                name: "plotArea",
                t,
                placeholder: "Enter plot Area"
              }}
            />
            <InputText
              {...{
                label: "CORE_COMMON_PERMISSBLE_COVERAGE",
                register,
                required: false,
                name: "maximumCoverage",
                t,
                placeholder: "Enter plot Area"
              }}
            />
          </Grid>
          <div>
            <p>
              <h2>
                <CardLabel>{`${t("BPA_DETAILS_OF_PLOT_AFTER_SUBDIVISION")} *`}</CardLabel>
              </h2>
            </p>
            <Card>
              <CardLabel>{`${t("BPA_TYPE_OF_LAND_VOLVED")} *`}</CardLabel>
              <RadioButtons
                style={{ display: 'flex', gap: '3rem', margin: 0 }}
                options={typeOfLandVolvedList}
                selectedOption={typeOfLandVolved}
                optionsKey="i18nKey"
                {...register(`typeOfLandVolved`)}
                name={'typeOfLandVolved'}
                onSelect={selectedValue}
                labelKey="PT_OWNERSHIP"
                isDependent={true}
              />

              {typeOfLandVolved?.code == "YES" && (
                <Select
                  {...{
                    label: "BPA_TYPE_OF_CLU_INVOLVED",
                    register,
                    required: false,
                    name: "typeOfCLUInvolved",
                    t,
                    options: typeOfLandVolvedYesList,
                    onChange: selectTypeOfCLUInvolved
                  }}
                  isDropDown
                />
              )}
              {typeOfLandVolved?.code == "YES" && typeOfCLUInvolved && (
                <div className="table table-bordered table-responsive" style={{ display: "block", overflowX: 'visible' }}>
                  <table>
                    <thead>
                      <tr>
                        <th className="fw-normal" style={{ minWidth: 50 }}>{`${t("BPA_SHORT_SERIAL_NO")}`}</th>
                        <th className="fw-normal">{`${t("BPA_PLOT_NO")} *`}</th>
                        <th className="fw-normal">{`${t("BPA_PLOT_AREA")} *`}</th>
                        <th className="fw-normal">{`${t("BPA_TYPE_OF_LAND_DEVELOPMENT")} *`}</th>
                        <th className="fw-normal">{`${t("BPA_ADD_MORE")}`}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {khasraTableFields.map((field, index) => {
                        return (
                          <tr key={field.id}>
                            <th className="fw-normal" style={{ minWidth: 50 }}>{index + 1}</th>
                            <td>
                              <TextInput
                                {...register(`khasraTable.${index}.plotNo`)}
                                name={`khasraTable.${index}.plotNo`}
                                t={t}
                                style={{ margin: 0 }}
                                type={"text"}
                                optionKey="i18nKey"
                                placeholder={"Enter Khasra No."}
                                disabled={false}
                                required={false}
                              />
                            </td>
                            <td>
                              <TextInput
                                {...register(`khasraTable.${index}.plotArea`)}
                                name={`khasraTable.${index}.plotArea`}
                                t={t}
                                style={{ margin: 0 }}
                                type={"text"}
                                optionKey="i18nKey"
                                placeholder={"Enter Area (sq.m)"}
                                disabled={false}
                                required={false}
                              />
                            </td>
                            <td>
                              <RadioOrSelect
                                {...register(`khasraTable.${index}.typeOfLand`)}
                                name={`khasraTable.${index}.typeOfLand`}
                                dropdownStyle={{ margin: 0 }}
                                optionCardStyles={{ maxHeight: "20vmax", overflow: "scroll" }}
                                options={useTypeList.sort((a, b) => a.name.localeCompare(b.name))}
                                optionKey="name"
                                // selectedValue={value={getValues(`khasraTable.${index}.typeOfLand`)}}
                                onSelect={val => setValue(`khasraTable.${index}.typeOfLand`, val)}
                                t={t}
                                isDependent={true}
                              />
                            </td>
                            <td>
                              <Box sx={{ display: 'flex', gap: '.5rem' }}>
                                <IconButton color="primary" aria-label="Add more" onClick={() => appendTable(khasraTableEntry)}>
                                  <AddBoxIcon />
                                </IconButton>
                                {khasraTableFields.length > 1 && (
                                  <IconButton color="error" aria-label="Delete" onClick={() => removeTable(index)}>
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </Box>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <InputText
                {...{
                  label: "CORE_COMMON_NO_OF_BLOCK",
                  register,
                  required: false,
                  name: "noOfBlocks",
                  t,
                  placeholder: "Enter Number of Blocks",
                }}
              />
              {Number(watchNoOfBlocks) > 0 && watchBuildUpAreaTable.map(({ table }, idx) => {
                return (
                  <div className="table table-bordered table-responsive" style={{ display: "block" }} key={`table_${idx}`}>
                    <table>
                      <thead>
                        <tr>
                          <th className="fw-normal" style={{ minWidth: 50 }}>{`${t("BPA_SHORT_SERIAL_NO")}`}</th>
                          <th className="fw-normal" style={{ minWidth: 150 }}>{`${t("BPA_FLOOR_TYPE")} *`}</th>
                          <th className="fw-normal" style={{ minWidth: 100 }}>{`${t("BPA_APPROVED_FLOOR_AREA")} *`}</th>
                          <th className="fw-normal" style={{ minWidth: 150 }}>{`${t("BPA_LAND_USE_TYPE")} *`}</th>
                          <th className="fw-normal">{`${t("BPA_PARKING_AREA")}`}</th>
                          <th className="fw-normal">{`${t("BPA_ACTUAL_CONSTRUCTION")}`}</th>
                          <th className="fw-normal">{`${t("BPA_PERMISSIBLE_DEVIATION")}`}</th>
                          <th className="fw-normal">{`${t("BPA_SETBACK_DEVIATION")}`}</th>
                          <th className="fw-normal">{`${t("DEVIATION_CENT")}`}</th>
                          <th className="fw-normal">&nbsp;</th>
                        </tr>
                      </thead>
                      <tbody>
                        {table?.map(({ id, ...field }, index) => {
                          return (
                            <tr key={`row_${idx}_${id}`}>
                              <th className="fw-normal" style={{ minWidth: 50 }}>{index + 1}</th>
                              <td>
                                <input
                                  type="hidden"
                                  value={watchBuildUpAreaTable[idx].table[id]?.id}
                                  name={`buildUpAreaTable[${idx}].table[${id}].id`}
                                  {...register(`buildUpAreaTable[${idx}].table[${id}].id`)}
                                />
                                <RadioOrSelect
                                  {...register(`buildUpAreaTable[${idx}].table[${id}].floorType`)}
                                  name={`buildUpAreaTable[${idx}].table[${id}].floorType`}
                                  dropdownStyle={{ margin: 0 }}
                                  optionCardStyles={{ maxHeight: "20vmax", overflow: "scroll" }}
                                  options={floorTypeList}
                                  optionKey="name"
                                  // selectedOption={field.floorType}
                                  selectedOption={`buildUpAreaTable[${idx}].table[${id}].floorType`}
                                  onSelect={val => updateBuildUpAreaData(idx, id, `floorType`, val)}
                                  t={t}
                                  isDependent={true}
                                />
                              </td>
                              <td>
                                <TextInput
                                  {...register(`buildUpAreaTable[${idx}].table[${id}].floorArea`)}
                                  name={`buildUpAreaTable[${idx}].table[${id}].floorArea`}
                                  onChange={(e) => updateBuildUpAreaData(idx, id, `floorArea`, e.target.value)}
                                  t={t}
                                  style={{ margin: 0 }}
                                  type={"text"}
                                  optionKey="i18nKey"
                                  placeholder={"Enter Approved Floor Area"}
                                  disabled={false}
                                  required={false}
                                  errorStyle={!!(showToast?.key === `floorArea_${idx}_${id}`)}
                                />
                              </td>
                              <td>
                                <RadioOrSelect
                                  {...register(`buildUpAreaTable[${idx}].table[${id}].landUseType`)}
                                  name={`buildUpAreaTable[${idx}].table[${id}].landUseType`}
                                  dropdownStyle={{ margin: 0 }}
                                  optionCardStyles={{ maxHeight: "20vmax", overflow: "scroll" }}
                                  options={useTypeList.sort((a, b) => a.name.localeCompare(b.name))}
                                  optionKey="name"
                                  // selectedOption={field.landUseType}
                                  selectedOption={`buildUpAreaTable[${idx}].table[${id}].landUseType`}
                                  onSelect={val => updateBuildUpAreaData(idx, id, `landUseType`, val)}
                                  t={t}
                                  isDependent={true}
                                />
                              </td>
                              <td>
                                <TextInput
                                  {...register(`buildUpAreaTable[${idx}].table[${id}].parkingArea`)}
                                  name={`buildUpAreaTable[${idx}].table[${id}].parkingArea`}
                                  onChange={(e) => updateBuildUpAreaData(idx, id, `parkingArea`, e.target.value)}
                                  t={t}
                                  style={{ margin: 0 }}
                                  type={"text"}
                                  optionKey="i18nKey"
                                  placeholder={"Enter parking area"}
                                  disabled={false}
                                  required={false}
                                />
                              </td>
                              <td>
                                <TextInput
                                  {...register(`buildUpAreaTable[${idx}].table[${id}].actualConstruction`)}
                                  name={`buildUpAreaTable[${idx}].table[${id}].actualConstruction`}
                                  onChange={(e) => updateBuildUpAreaData(idx, id, `actualConstruction`, e.target.value)}
                                  t={t}
                                  style={{ margin: 0 }}
                                  type={"text"}
                                  optionKey="i18nKey"
                                  placeholder={"Enter actual construction"}
                                  disabled={false}
                                  required={false}
                                />
                              </td>
                              <td>
                                <TextInput
                                  {...register(`buildUpAreaTable[${idx}].table[${id}].permissibleDeviation`)}
                                  name={`buildUpAreaTable[${idx}].table[${id}].permissibleDeviation`}
                                  // onChange={(e) => updateBuildUpAreaData(idx, id, `permissibleDeviation`, e.target.value)}
                                  t={t}
                                  style={{ margin: 0 }}
                                  type={"text"}
                                  optionKey="i18nKey"
                                  placeholder={"Enter permissible deviation"}
                                  disabled={true}
                                  required={false}
                                />
                              </td>
                              <td>
                                <TextInput
                                  {...register(`buildUpAreaTable[${idx}].table[${id}].setbackDeviation`)}
                                  name={`buildUpAreaTable[${idx}].table[${id}].setbackDeviation`}
                                  onChange={(e) => updateBuildUpAreaData(idx, id, `setbackDeviation`, e.target.value)}
                                  t={t}
                                  style={{ margin: 0 }}
                                  type={"text"}
                                  optionKey="i18nKey"
                                  placeholder={"Enter permissible deviation"}
                                  disabled={false}
                                  required={false}
                                />
                              </td>
                              <td>
                                <TextInput
                                  {...register(`buildUpAreaTable[${idx}].table[${id}].deviation`)}
                                  name={`buildUpAreaTable[${idx}].table[${id}].deviation`}
                                  onChange={(e) => updateBuildUpAreaData(idx, id, `deviation`, e.target.value)}
                                  t={t}
                                  style={{ margin: 0 }}
                                  type={"text"}
                                  optionKey="i18nKey"
                                  placeholder={"Enter deviation %"}
                                  disabled={false}
                                  required={false}
                                />
                              </td>
                              <td>
                                <Box sx={{ display: 'flex', gap: '.5rem' }}>
                                  <IconButton color="primary" aria-label="Add more" onClick={() => addTableRow(idx)}>
                                    <AddBoxIcon />
                                  </IconButton>
                                  {table.length > 1 && (
                                    <IconButton color="error" aria-label="Delete" onClick={() => removeTableRow(idx, id)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </Box>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              })}
            </Card>
          </div>
        </div>

        <SubmitBar label={t(`CS_COMMON_NEXT`)} onSubmit={handleSubmit(handleOnSubmit)} style={{ marginTop: '1rem' }} />
      </FormStep>

      {showToast && (
        <Toast
          isDleteBtn={true}
          error={showToast.error}
          warning={showToast.warning}
          label={t(showToast.label)}
          onClose={() => {
            setShowToast(null);
          }}
        />
      )}
    </div>
  )
};

export default DescriptionOfLand;

